import React, { useRef } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { useT } from '../../i18n/LanguageContext';

// Localized labels per bulletin language (fr / pt) — independent of the app UI language,
// because the printed bulletin must match the real-world document language.
const LABELS = {
  fr: {
    reportTitle: 'BULLETIN DE NOTES',
    generalTitle: 'BULLETIN GÉNÉRAL',
    year: 'Année scolaire', name: 'Prénom et Nom', klass: 'Classe', section: 'Section',
    effectif: 'Effectif', matricule: 'Matricule',
    uv: 'Unité de Valeur', matiere: 'MATIÈRES', discipline: 'Discipline', note: 'Note', notes: 'Notes /',
    cc: 'C.C', comp: 'COMP', mtotal: 'M. Total',
    total: 'TOTAL', moyenne: 'Moyenne', moyenneSur20: 'Moyenne /20',
    moyenneAnnuelle: 'MOYENNE ANNUELLE',
    teacherObs: 'Observation du professeur titulaire', direction: 'Direction',
    council: 'Observation du conseil pédagogique',
    mention: 'Mention', decision: 'Décision',
    trim: (n) => `${n}${n === 1 ? 'er' : 'ème'} TRIM.`,
    mentions: { tres_bien: 'Très Bien', bien: 'Bien', assez_bien: 'Assez Bien', passable: 'Passable', insuffisant: 'Insuffisant' },
    decisions: { passe: 'Passe en classe supérieure', redouble: 'Redouble' },
  },
  pt: {
    reportTitle: 'BOLETIM DE NOTAS',
    generalTitle: 'BOLETIM GERAL',
    year: 'Ano letivo', name: 'Nome do Aluno', klass: 'Turma', section: 'Seção',
    effectif: 'Efectivos', matricule: 'CDM',
    uv: 'Unidade Curricular', matiere: 'DISCIPLINAS', discipline: 'Disciplina', note: 'Nota', notes: 'Notas /',
    cc: 'C.C', comp: 'COMP', mtotal: 'M. Total',
    total: 'TOTAL', moyenne: 'Média', moyenneSur20: 'Média /20',
    moyenneAnnuelle: 'MÉDIA ANUAL',
    teacherObs: 'Observação do professor Titular', direction: 'Direção',
    council: 'Observação do conselho pedagógico',
    mention: 'Menção', decision: 'Decisão Final',
    trim: (n) => `${n}º TRIM.`,
    mentions: { tres_bien: 'Muito Bom', bien: 'Bom', assez_bien: 'Suficiente', passable: 'Aprovado', insuffisant: 'Insuficiente' },
    decisions: { passe: 'Aprovado(a) — Passa para o nível superior', redouble: 'Reprovado(a) — Repete o ano' },
  },
};

const fmtNum = (v) => (v === null || v === undefined || v === '' ? '' : (Math.round(v * 100) / 100));

const MetaRow = ({ label, value, testid }) => (
  <div className="flex gap-1">
    <span className="text-[11px] text-gray-600">{label}:</span>
    <span className="text-[11px] font-semibold" data-testid={testid}>{value || '—'}</span>
  </div>
);

const Header = ({ card, L }) => (
  <div className="flex items-center gap-4 border-b-2 border-[color:var(--eli-crest-navy,#1e3a5f)] pb-3">
    <img src={card.school_logo_url || '/eli-crest.png'} alt={card.school_name || 'Logo'} className="w-16 h-16 object-contain" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
    <div className="flex-1 text-center">
      <h2 className="text-base font-bold uppercase tracking-wide text-[color:var(--eli-crest-navy,#1e3a5f)] leading-tight">
        {card.school_name}
      </h2>
      <p className="text-xs font-semibold mt-0.5">
        {card.is_general ? L.generalTitle : L.reportTitle}
        {!card.is_general && card.term_columns?.length === 1 && card.term_columns[0]?.name ? ` — ${card.term_columns[0].name}` : ''}
      </p>
    </div>
    <div className="text-right">
      <p className="text-[11px] text-gray-600">{L.year}</p>
      <p className="text-xs font-semibold">{card.academic_year_name || '—'}</p>
    </div>
  </div>
);

const StudentMeta = ({ card, L }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3">
    <MetaRow label={L.name} value={card.student_name} testid="report-card-student-name" />
    <MetaRow label={L.klass} value={card.class_name} />
    <MetaRow label={L.section} value={card.section_name} />
    <MetaRow label={L.effectif} value={card.effectif} />
    <MetaRow label={L.matricule} value={card.matricule} />
  </div>
);

const cellCls = 'border border-gray-400 px-2 py-1';

// ---------- Primary layout (single trimester; FR shows CC/COMP, PT shows only note) ----------
// Grouped table where the first column shows the group label once (rowSpan)
const PrimaryGroupedTable = ({ card, L, showComponents }) => {
  const seq = card.term_columns?.[0]?.seq;
  const cur = card.per_term?.[String(seq)] || {};
  const colCount = showComponents ? 6 : 4;
  // Column totals for the C.C and Comp components (sum across all disciplines,
  // skipping absents). Only meaningful when components are shown.
  let totalCC = 0, totalComp = 0, hasCC = false, hasComp = false;
  if (showComponents) {
    (card.groups || []).forEach((g) => (g.disciplines || []).forEach((d) => {
      const td = d.terms?.[seq] || {};
      if (td.is_absent) return;
      if (td.cc !== null && td.cc !== undefined) { totalCC += Number(td.cc); hasCC = true; }
      if (td.comp !== null && td.comp !== undefined) { totalComp += Number(td.comp); hasComp = true; }
    }));
  }
  // Moyenne /20 per component = (component total / total max) × 20
  const ccMoy20 = (hasCC && cur.total_max) ? Math.round((totalCC / cur.total_max) * 20 * 100) / 100 : null;
  const compMoy20 = (hasComp && cur.total_max) ? Math.round((totalComp / cur.total_max) * 20 * 100) / 100 : null;
  return (
    <table className="w-full mt-4 text-[12px] border-collapse" data-testid="bulletin-primary-table">
      <thead>
        <tr className="bg-gray-100">
          <th className={`${cellCls} text-left`}>{L.uv}</th>
          <th className={`${cellCls} text-left`}>{L.discipline}</th>
          <th className={`${cellCls} text-center w-16`}>{L.notes}</th>
          {showComponents && <th className={`${cellCls} text-center w-14`}>{L.cc}</th>}
          {showComponents && <th className={`${cellCls} text-center w-14`}>{L.comp}</th>}
          <th className={`${cellCls} text-center w-16`}>{L.mtotal}</th>
        </tr>
      </thead>
      <tbody>
        {(card.groups || []).map((g, gi) => (
          g.disciplines.map((d, di) => {
            const td = d.terms?.[seq] || {};
            return (
              <tr key={`${gi}-${di}`} data-testid={`bulletin-disc-${d.subject_id}`}>
                {di === 0 && (
                  <td className={`${cellCls} align-top font-semibold`} rowSpan={g.disciplines.length}>
                    {g.label || '—'}{g.max ? <span className="block text-[10px] text-gray-500">/ {fmtNum(g.max)}</span> : null}
                  </td>
                )}
                <td className={cellCls}>{d.subject_name}</td>
                <td className={`${cellCls} text-center`}>{fmtNum(d.max_note)}</td>
                {showComponents && <td className={`${cellCls} text-center`}>{td.cc_absent ? 'AB' : fmtNum(td.cc)}</td>}
                {showComponents && <td className={`${cellCls} text-center`}>{td.is_absent ? '' : (td.comp_absent ? 'AB' : fmtNum(td.comp))}</td>}
                <td className={`${cellCls} text-center font-medium`}>{td.is_absent ? 'AB' : fmtNum(td.note)}</td>
              </tr>
            );
          })
        ))}
        <tr className="bg-gray-100 font-bold">
          <td className={cellCls} colSpan={2}>{L.total}</td>
          <td className={`${cellCls} text-center`}>{fmtNum(cur.total_max)}</td>
          {showComponents && <td className={`${cellCls} text-center`} data-testid="bulletin-total-cc">{hasCC ? fmtNum(totalCC) : ''}</td>}
          {showComponents && <td className={`${cellCls} text-center`} data-testid="bulletin-total-comp">{hasComp ? fmtNum(totalComp) : ''}</td>}
          <td className={`${cellCls} text-center`}>{fmtNum(cur.total_obtained)}</td>
        </tr>
        <tr className="bg-gray-100 font-bold">
          <td className={cellCls} colSpan={2}>{L.moyenneSur20}</td>
          <td className={`${cellCls} text-center`}>20</td>
          {showComponents && <td className={`${cellCls} text-center`} data-testid="bulletin-moy-cc">{ccMoy20 != null ? fmtNum(ccMoy20) : ''}</td>}
          {showComponents && <td className={`${cellCls} text-center`} data-testid="bulletin-moy-comp">{compMoy20 != null ? fmtNum(compMoy20) : ''}</td>}
          <td className={`${cellCls} text-center`} data-testid="bulletin-moyenne">{fmtNum(card.moyenne_sur_20)}</td>
        </tr>
      </tbody>
    </table>
  );
};

// ---------- Secondary layout (cumulative: 3 trimesters with CC/COMP each) ----------
const SecondaryTable = ({ card, L }) => {
  const cols = card.term_columns || [];
  return (
    <table className="w-full mt-4 text-[11px] border-collapse" data-testid="bulletin-secondary-table">
      <thead>
        <tr className="bg-gray-100">
          <th className={`${cellCls} text-left`} rowSpan={2}>{L.uv}</th>
          <th className={`${cellCls} text-left`} rowSpan={2}>{L.matiere}</th>
          <th className={`${cellCls} text-center w-12`} rowSpan={2}>{L.note}</th>
          {cols.map((c) => (
            <th key={c.seq} className={`${cellCls} text-center`} colSpan={2}>{L.trim(c.seq)}</th>
          ))}
        </tr>
        <tr className="bg-gray-100">
          {cols.map((c) => (
            <React.Fragment key={c.seq}>
              <th className={`${cellCls} text-center w-10`}>{L.cc}</th>
              <th className={`${cellCls} text-center w-10`}>{L.comp}</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {(card.groups || []).map((g, gi) => (
          g.disciplines.map((d, di) => (
            <tr key={`${gi}-${di}`} data-testid={`bulletin-disc-${d.subject_id}`}>
              {di === 0 && (
                <td className={`${cellCls} align-top font-semibold`} rowSpan={g.disciplines.length}>{g.label || '—'}</td>
              )}
              <td className={cellCls}>{d.subject_name}</td>
              <td className={`${cellCls} text-center`}>{fmtNum(d.max_note)}</td>
              {cols.map((c) => {
                const td = d.terms?.[c.seq] || {};
                return (
                  <React.Fragment key={c.seq}>
                    <td className={`${cellCls} text-center`}>{td.cc_absent ? 'AB' : fmtNum(td.cc)}</td>
                    <td className={`${cellCls} text-center`}>{td.is_absent ? '' : (td.comp_absent ? 'AB' : fmtNum(td.comp))}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))
        ))}
        <tr className="bg-gray-100 font-bold">
          <td className={cellCls} colSpan={3}>{L.moyenne} /20</td>
          {cols.map((c) => {
            const pt = card.per_term?.[String(c.seq)] || {};
            return <td key={c.seq} className={`${cellCls} text-center`} colSpan={2} data-testid={`bulletin-term-moy-${c.seq}`}>{fmtNum(pt.moyenne)}</td>;
          })}
        </tr>
      </tbody>
    </table>
  );
};

// Reusable Bulletin view (on-screen + print). Pass a `card` object from the API.
export const ReportCardView = ({ card, showPrint = true }) => {
  const { t } = useT();
  const ref = useRef(null);

  if (!card) return null;
  const L = LABELS[card.language] || LABELS.fr;
  const fmt = card.bulletin_format;
  const multiTerm = (card.term_columns || []).length > 1;
  const mentionLabel = card.mention ? (L.mentions[card.mention] || card.mention) : '—';
  const decisionRaw = card.decision;
  const decisionLabel = decisionRaw ? (L.decisions[decisionRaw] || decisionRaw) : '';
  const teacherObs = card.remarks?.teacher_observation || '';

  return (
    <div data-testid="report-card-sheet">
      {showPrint && (
        <div className="flex justify-end mb-3 no-print">
          <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-2" data-testid="report-card-print-button">
            <Printer className="w-4 h-4" />
            {t('common.print')}
          </Button>
        </div>
      )}

      <div ref={ref} className="report-card-sheet print-area bg-white text-gray-900 border border-gray-300 rounded-md p-4 sm:p-8 max-w-3xl mx-auto">
        <Header card={card} L={L} />
        <StudentMeta card={card} L={L} />

        <div className="overflow-x-auto -mx-1 px-1">
          {multiTerm ? (
            <SecondaryTable card={card} L={L} />
          ) : (
            <PrimaryGroupedTable card={card} L={L} showComponents={fmt === 'primary_fr' || fmt === 'primary_pt'} />
          )}
        </div>

        {/* Summary: mention / annual avg / decision */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          <div className="border border-gray-300 rounded p-2">
            <p className="text-[10px] text-gray-500 uppercase">{L.moyenneSur20}</p>
            <p className="font-bold tabular-nums" data-testid="bulletin-summary-moyenne">{fmtNum(card.moyenne_sur_20) || '—'}</p>
          </div>
          {multiTerm && card.moyenne_annuelle != null && (
            <div className="border border-gray-300 rounded p-2">
              <p className="text-[10px] text-gray-500 uppercase">{L.moyenneAnnuelle}</p>
              <p className="font-bold tabular-nums" data-testid="bulletin-annual">{fmtNum(card.moyenne_annuelle) || '—'}</p>
            </div>
          )}
          <div className="border border-gray-300 rounded p-2">
            <p className="text-[10px] text-gray-500 uppercase">{L.mention}</p>
            <p className="font-bold" data-testid="bulletin-mention">{mentionLabel}</p>
          </div>
        </div>

        {/* Observations & Decision (read-only — as saved by admin/teacher) */}
        <div className="mt-4 space-y-2" data-testid="bulletin-observations">
          <div className="border border-gray-300 rounded p-2">
            <p className="text-[10px] text-gray-500 uppercase">{L.teacherObs}</p>
            <p className="text-[12px] whitespace-pre-wrap min-h-[16px]" data-testid="bulletin-teacher-observation">{teacherObs}</p>
          </div>
          <div className="border border-gray-300 rounded p-2">
            <p className="text-[10px] text-gray-500 uppercase">{L.decision}</p>
            <p className="text-[12px] font-semibold min-h-[16px]" data-testid="bulletin-decision">{decisionLabel}</p>
          </div>
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-8 mt-10 text-[12px]">
          <div />
          <div className="text-center">
            {card.school_seal_url && (
              <img
                src={card.school_seal_url}
                alt="Seal"
                className="mx-auto mb-1 object-contain"
                style={{ width: '4cm', height: '4cm', backgroundColor: 'transparent' }}
                data-testid="bulletin-seal"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="border-t border-gray-500 pt-1">{L.direction}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
